import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import { observarEstoque, salvarPrecoFiscal } from "services/estoqueService";
import { salvarHistoricoFiscal } from "services/fiscalService";
import { db } from "services/firebaseConfig";
import { collection, query, orderBy, onSnapshot, where, Timestamp } from "firebase/firestore";
import {
  Plus,
  Trash2,
  Calculator,
  Send,
  ArrowRight,
  ArrowLeft,
  History,
  FileText,
  Download,
  Printer,
  Eye,
  ExternalLink,
  RefreshCw,
  Check,
  Copy,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  DollarSign,
  Receipt,
  Layers,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  FileArchive,
  Loader2,
} from "lucide-react";
import {
  baixarXmlIndividual,
  baixarLoteZip,
  baixarPeriodoZip,
} from "services/xmlExportService";

export default function FechamentoFiscalPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [historicoNotas, setHistoricoNotas] = useState([]);

  // Filtros Histórico
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [filtroRapido, setFiltroRapido] = useState("todos");
  const [mostrarFiltroAvancado, setMostrarFiltroAvancado] = useState(false);

  // Estado de Exportação de XMLs em Lote / ZIP
  const [exportandoZip, setExportandoZip] = useState(false);
  const [statusExportacao, setStatusExportacao] = useState({
    current: 0,
    total: 0,
    percent: 0,
    status: "",
  });

  // Passo 1: Recebimentos
  const [entradas, setEntradas] = useState([
    { id: 1, nome: "Cartão de Crédito", valor: 0 },
  ]);
  const [acrescimoEspecie, setAcrescimoEspecie] = useState(0);

  // Passo 2: Rateio Serviço vs Consumo
  const [percentualServico, setPercentualServico] = useState(0);

  // Passo 3: Distribuição
  const [qtdNotas, setQtdNotas] = useState(1);
  const [percentualAlmoco, setPercentualAlmoco] = useState(100);
  const [precoBaseAlmoco, setPrecoBaseAlmoco] = useState(50);

  // Estoque ao vivo
  const [estoque, setEstoque] = useState([]);
  const [estoqueCompleto, setEstoqueCompleto] = useState([]);
  const [bebidasSelecionadas, setBebidasSelecionadas] = useState({});

  // Confirmacao
  const [loteConfirmacao, setLoteConfirmacao] = useState(null);
  const [isEnviando, setIsEnviando] = useState(false);

  // Identificação do Cliente
  const [identificarCliente, setIdentificarCliente] = useState(false);
  const [clienteCpfCnpj, setClienteCpfCnpj] = useState("");
  const [clienteNome, setClienteNome] = useState("");

  // Modal de Detalhes / Status SEFAZ
  const [loteModal, setLoteModal] = useState(null);
  const [statusNotasLote, setStatusNotasLote] = useState({});
  const [carregandoStatus, setCarregandoStatus] = useState(false);
  const [copiadoChave, setCopiadoChave] = useState(null);

  useEffect(() => {
    const unsubEstoque = observarEstoque((lista) => {
      setEstoqueCompleto(lista);
      const bebidas = lista.filter((item) => item.ncm && item.ncm.startsWith("22"));
      setEstoque(bebidas);
    });
    return () => unsubEstoque();
  }, []);

  useEffect(() => {
    const constraints = [orderBy("dataEmissao", "desc")];
    
    if (dataInicio) {
      constraints.push(where("dataEmissao", ">=", Timestamp.fromDate(new Date(`${dataInicio}T00:00:00`))));
    }
    if (dataFim) {
      constraints.push(where("dataEmissao", "<=", Timestamp.fromDate(new Date(`${dataFim}T23:59:59`))));
    }

    const q = query(collection(db, "historico_notas_fiscais"), ...constraints);
    const unsubHistorico = onSnapshot(q, (snap) => {
      setHistoricoNotas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubHistorico();
  }, [dataInicio, dataFim]);

  const aplicarFiltroRapido = (tipo) => {
    setFiltroRapido(tipo);
    const hoje = new Date();
    const fmt = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (tipo === "todos") {
      setDataInicio("");
      setDataFim("");
      setMostrarFiltroAvancado(false);
    } else if (tipo === "hoje") {
      const hStr = fmt(hoje);
      setDataInicio(hStr);
      setDataFim(hStr);
      setMostrarFiltroAvancado(false);
    } else if (tipo === "7dias") {
      const seteDiasAtras = new Date(hoje.getTime() - 7 * 24 * 60 * 60 * 1000);
      setDataInicio(fmt(seteDiasAtras));
      setDataFim(fmt(hoje));
      setMostrarFiltroAvancado(false);
    } else if (tipo === "mes") {
      const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      setDataInicio(fmt(primeiroDia));
      setDataFim(fmt(hoje));
      setMostrarFiltroAvancado(false);
    } else if (tipo === "custom") {
      setMostrarFiltroAvancado((prev) => !prev);
    }
  };

  const limparFiltros = () => {
    setDataInicio("");
    setDataFim("");
    setFiltroRapido("todos");
    setMostrarFiltroAvancado(false);
  };

  const getResumoLote = (lote) => {
    const notasComSucesso = (lote.retornoNotaaS || []).filter(
      (r) => (r.status >= 200 && r.status < 300) || r.status === 202,
    );

    let valor = 0;
    const valorNotaaS = notasComSucesso.reduce((acc, n) => {
      return acc + Number(n.data?.valorTotal || n.data?.vNf || 0);
    }, 0);

    if (valorNotaaS > 0) {
      valor = valorNotaaS;
    } else {
      valor = (lote.lotePayload || []).reduce((pAcc, p) => {
        const pag = p.pagamentos?.[0]?.valor;
        if (pag) return pAcc + Number(pag);
        return (
          pAcc +
          (p.items || []).reduce(
            (iAcc, it) => iAcc + Number(it.valorTotal || 0),
            0,
          )
        );
      }, 0);
    }

    const primeiroPayload = lote.lotePayload?.[0];
    const cliente =
      primeiroPayload?.dest?.cpf || primeiroPayload?.dest?.cnpj
        ? primeiroPayload.dest.nome || "Cliente com CPF/CNPJ"
        : "Consumidor Final";

    const itensCount = (primeiroPayload?.items || []).length;
    const nomesItens = (primeiroPayload?.items || [])
      .map((it) => it.descricao)
      .filter(Boolean);
    const textoItens =
      nomesItens.length > 0
        ? nomesItens.slice(0, 3).join(", ") + (nomesItens.length > 3 ? "..." : "")
        : "Produtos emitidos";

    return {
      valor,
      cliente,
      itensCount,
      textoItens,
      qtdNotasValidas: notasComSucesso.length,
      isSucesso: notasComSucesso.length > 0,
    };
  };

  const formatarDataAmigavel = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return "Sincronizando...";
    const d = timestamp.toDate();
    const hoje = new Date();
    const ehHoje = d.toDateString() === hoje.toDateString();

    const ontem = new Date();
    ontem.setDate(hoje.getDate() - 1);
    const ehOntem = d.toDateString() === ontem.toDateString();

    const horaStr = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    if (ehHoje) return `Hoje às ${horaStr}`;
    if (ehOntem) return `Ontem às ${horaStr}`;
    return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" })} às ${horaStr}`;
  };

  const totalLotes = historicoNotas.length;
  const totalNotasGeral = historicoNotas.reduce(
    (acc, l) => acc + (Number(l.notasEnviadas) || 0),
    0,
  );

  const totalValorGeral = historicoNotas.reduce((acc, l) => {
    const { valor } = getResumoLote(l);
    return acc + valor;
  }, 0);


  const atualizarBebida = (id, campo, valor) => {
    const valorEfetivo = valor < 0 ? 0 : valor;
    setBebidasSelecionadas((prev) => {
      const bebida = estoque.find((b) => b.id === id);
      const atual = prev[id] || { qtd: 0, preco: bebida ? bebida.precoFiscal || 0 : 0 };
      return { ...prev, [id]: { ...atual, [campo]: valorEfetivo } };
    });
  };

  const totalEntradas = entradas.reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);
  const totalComEspecie = totalEntradas * (1 + acrescimoEspecie / 100);
  const totalServico = totalComEspecie * (percentualServico / 100);
  const totalConsumo = totalComEspecie - totalServico;

  const qtdNotasEfetiva = identificarCliente ? 1 : qtdNotas;
  const valorPorNota = totalConsumo / qtdNotasEfetiva;
  const totalAlmocoUnitario = valorPorNota * (percentualAlmoco / 100);
  const totalBebidasFinanceiroUnitario = valorPorNota - totalAlmocoUnitario;

  const totalBebidasCalculadoUnitario = estoque.reduce((acc, bebida) => {
    const sel = bebidasSelecionadas[bebida.id] || { qtd: 0, preco: bebida.precoFiscal || 0 };
    return acc + Number(sel.qtd) * Number(sel.preco);
  }, 0);

  const totalAlmocoEfetivoUnitario = valorPorNota - totalBebidasCalculadoUnitario;
  const totalGeralEmitido = valorPorNota * qtdNotasEfetiva;

  const adicionarEntrada = () => setEntradas([...entradas, { id: Date.now(), nome: `Entrada ${entradas.length + 1}`, valor: 0 }]);
  const removerEntrada = (id) => setEntradas(entradas.filter((e) => e.id !== id));
  const atualizarEntrada = (id, campo, valor) => setEntradas(entradas.map((e) => (e.id === id ? { ...e, [campo]: valor } : e)));

  const preAvancarStep4 = () => {
    if (totalConsumo <= 0 || qtdNotas < 1) {
      alert("Preencha os valores antes de avançar.");
      return;
    }
    if (totalAlmocoEfetivoUnitario < 0) {
      alert("O valor das bebidas selecionadas ultrapassou o valor de UMA nota!");
      return;
    }

    const produtosBebidas = estoque.map((bebida) => {
      const sel = bebidasSelecionadas[bebida.id];
      if (!sel || !sel.qtd || !sel.preco || Number(sel.qtd) <= 0 || Number(sel.preco) <= 0) return null;
      return {
        id: bebida.id,
        codigo: bebida.codigo || bebida.id,
        nome: bebida.nome,
        ncm: bebida.ncm,
        cfop: bebida.cfop || "5405",
        csosn: bebida.csosn || "500",
        cest: bebida.cest || "",
        quantidade: Number(sel.qtd),
        precoUnitario: Number(sel.preco),
        subtotal: Number(sel.qtd) * Number(sel.preco),
      };
    }).filter(Boolean);

    const precoUnitarioAlmocoAplicado = precoBaseAlmoco > 0 ? precoBaseAlmoco : 1;
    const qtdeAlmocoCalculada = totalAlmocoEfetivoUnitario / precoUnitarioAlmocoAplicado;

    const itemAlmocoCadastrado = estoqueCompleto.find(
      (item) =>
        item.id === "00000001" ||
        item.codigo === "00000001" ||
        item.nome?.toUpperCase().includes("ALMOCO"),
    );

    const produtoAlmoco = {
      codigo: itemAlmocoCadastrado?.codigo || itemAlmocoCadastrado?.id || "00000001",
      nome: itemAlmocoCadastrado?.nome || "ALMOCO",
      ncm: itemAlmocoCadastrado?.ncm && itemAlmocoCadastrado.ncm !== "00000000" && itemAlmocoCadastrado.ncm !== "N/A"
        ? itemAlmocoCadastrado.ncm
        : "21069090",
      cfop: itemAlmocoCadastrado?.cfop || "5102",
      csosn: itemAlmocoCadastrado?.csosn || "103",
      origem: "0",
      quantidade: Number(qtdeAlmocoCalculada.toFixed(4)),
      precoUnitario: precoUnitarioAlmocoAplicado,
      subtotal: totalAlmocoEfetivoUnitario,
    };

    setLoteConfirmacao({
      qtdNotas: qtdNotasEfetiva, valorPorNota, totalGeralConsumo: totalConsumo,
      totalGeralEmitido: totalGeralEmitido, bebidas: produtosBebidas, almoco: produtoAlmoco,
    });
    
    setCurrentStep(4);
  };

  const REMOTE_SERVER_URL = process.env.REACT_APP_SEFAZ_URL || "https://reservas.parquetimbo.com.br";

  const confirmarEnvio = async () => {
    setIsEnviando(true);
    try {
      const notasParaAPI = [];
      for (let i = 0; i < loteConfirmacao.qtdNotas; i++) {
        const itemsNota = [];

        // 1. Almoço
        if (
          loteConfirmacao.almoco &&
          loteConfirmacao.almoco.quantidade > 0 &&
          loteConfirmacao.almoco.subtotal > 0
        ) {
          const vUnit = Number(
            (loteConfirmacao.almoco.precoUnitario || 1).toFixed(2),
          );
          const vTotal = Number(loteConfirmacao.almoco.subtotal.toFixed(2));
          const qtd = Number((vTotal / vUnit).toFixed(4));
          const vTotalAjustado = Number((qtd * vUnit).toFixed(2));

          itemsNota.push({
            codigo: String(loteConfirmacao.almoco.codigo || "00000001").trim(),
            descricao: (loteConfirmacao.almoco.nome || "ALMOCO")
              .trim()
              .substring(0, 120),
            ncm: String(loteConfirmacao.almoco.ncm || "21069090").replace(
              /\D/g,
              "",
            ),
            cfop: String(loteConfirmacao.almoco.cfop || "5102"),
            unidade: "UN",
            quantidade: qtd,
            valorUnitario: vUnit,
            valorTotal: vTotalAjustado,
            csosn: String(loteConfirmacao.almoco.csosn || "103"),
          });
        }

        // 2. Bebidas
        (loteConfirmacao.bebidas || []).forEach((b, idx) => {
          if (b.quantidade > 0 && b.precoUnitario > 0) {
            const vUnitBebida = Number(b.precoUnitario.toFixed(2));
            const qtdBebida = Number(b.quantidade);
            const vTotalBebida = Number((qtdBebida * vUnitBebida).toFixed(2));

            const itemBebida = {
              codigo: String(b.codigo || b.id || `00${idx + 2}`).trim(),
              descricao: (b.nome || "Bebida").trim().substring(0, 120),
              ncm: String(b.ncm || "22021000").replace(/\D/g, ""),
              cfop: String(b.cfop || "5405"),
              unidade: "UN",
              quantidade: qtdBebida,
              valorUnitario: vUnitBebida,
              valorTotal: vTotalBebida,
              csosn: String(b.csosn || "500"),
            };

            if (b.cest) {
              const cestLimpo = String(b.cest).replace(/\D/g, "");
              if (cestLimpo) {
                itemBebida.cest = cestLimpo;
              }
            }

            itemsNota.push(itemBebida);
          }
        });

        // O valor pago deve bater rigorosamente com a soma de valorTotal dos itens
        const totalItens = itemsNota.reduce((acc, it) => acc + it.valorTotal, 0);
        const valorNotaFinal = Number(totalItens.toFixed(2));

        const payload = {
          modelo: 65,
          naturezaOperacao: "Venda a consumidor",
          items: itemsNota,
          pagamentos: [
            {
              tipoPagamento: "01",
              valor: valorNotaFinal,
            },
          ],
        };

        // Na NFC-e modelo 65, o dest é opcional. Inclui apenas se solicitado.
        if (identificarCliente && clienteCpfCnpj) {
          const docLimpo = clienteCpfCnpj.replace(/\D/g, "");
          if (docLimpo) {
            const destObj = {};
            if (docLimpo.length <= 11) {
              destObj.cpf = docLimpo;
            } else {
              destObj.cnpj = docLimpo;
            }
            destObj.nome =
              clienteNome && clienteNome.trim().length >= 2
                ? clienteNome.trim().substring(0, 60)
                : "CONSUMIDOR FINAL";

            payload.dest = destObj;
          }
        }

        notasParaAPI.push(payload);
      }

      const baseUrl =
        window.location.hostname === "localhost"
          ? "http://localhost:3001"
          : window.location.hostname.includes("parquetimbo.com.br")
            ? ""
            : REMOTE_SERVER_URL;

      const res = await fetch(`${baseUrl}/api/fiscal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notas: notasParaAPI }),
      });
      const data = await res.json();

      if (data.success) {
        const resultados = data.resultados || [];
        const sucessos = resultados.filter(
          (r) => (r.status >= 200 && r.status < 300) || r.status === 202,
        );
        const falhas = resultados.filter(
          (r) => !((r.status >= 200 && r.status < 300) || r.status === 202),
        );

        if (sucessos.length > 0) {
          await salvarHistoricoFiscal(notasParaAPI, resultados);
          if (falhas.length === 0) {
            alert(
              `✅ Sucesso! ${sucessos.length} nota(s) enviada(s) à SEFAZ/NotaaS com sucesso e arquivada(s).`,
            );
          } else {
            const detalheFalha =
              falhas[0]?.data?.message ||
              falhas[0]?.data?.erro ||
              falhas[0]?.data?.error ||
              JSON.stringify(falhas[0]?.data || falhas[0]?.raw);
            alert(
              `⚠️ Parcial: ${sucessos.length} nota(s) emitida(s), mas ${falhas.length} falharam.\nDetalhe: ${detalheFalha}`,
            );
          }
          // Reseta o formulário após o sucesso
          setCurrentStep(1);
          setEntradas([{ id: 1, nome: "Cartão de Crédito", valor: 0 }]);
        } else {
          // Todas as notas falharam na NotaaS
          const primeiroErro = falhas[0] || {};
          let msgErro =
            primeiroErro.data?.message ||
            primeiroErro.data?.erro ||
            primeiroErro.data?.erros ||
            primeiroErro.data?.error ||
            primeiroErro.raw ||
            `Status HTTP ${primeiroErro.status}`;

          if (
            Array.isArray(primeiroErro.data?.campos) &&
            primeiroErro.data.campos.length > 0
          ) {
            msgErro +=
              "\n\nCampos ausentes ou inválidos:\n• " +
              primeiroErro.data.campos.join("\n• ");
          }

          alert(
            `❌ Rejeição NotaaS (HTTP ${primeiroErro.status || "Erro"}):\n\n${typeof msgErro === "object" ? JSON.stringify(msgErro, null, 2) : msgErro}`,
          );
        }
      } else {
        alert("Erro no backend: " + (data.message || "Desconhecido"));
      }
    } catch (err) {
      alert("Falha de rede ao contatar o servidor local/nuvem: " + err.message);
    } finally {
      setIsEnviando(false);
    }
  };

  const step1Content = (
    <div className="space-y-6 fade-in">
      <Card>
        <CardHeader>
          <CardTitle>Passo 1: Entradas Financeiras</CardTitle>
          <CardDescription>Insira os valores recebidos via PIX, Cartões, etc.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {entradas.map((entrada) => (
            <div key={entrada.id} className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-start sm:items-center border-b sm:border-0 pb-4 sm:pb-0">
              <Input value={entrada.nome} onChange={(e) => atualizarEntrada(entrada.id, "nome", e.target.value)} placeholder="Ex: Cartão de Crédito" className="w-full sm:flex-1" />
              <div className="flex gap-2 w-full sm:w-auto items-center">
                <div className="relative flex-1 sm:w-32">
                  <span className="absolute left-3 top-2.5 text-gray-500">R$</span>
                  <Input type="number" inputMode="decimal" value={entrada.valor || ""} onChange={(e) => atualizarEntrada(entrada.id, "valor", e.target.value)} className="w-full pl-8 h-12 sm:h-10 text-lg sm:text-base" />
                </div>
                <Button variant="ghost" size="icon" onClick={() => removerEntrada(entrada.id)} className="flex-shrink-0 w-12 h-12 sm:w-10 sm:h-10">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={adicionarEntrada} className="w-full h-12 sm:h-10"><Plus className="w-4 h-4 mr-2" /> Adicionar Linha</Button>
          <div className="pt-4 border-t">
            <Label>Acréscimo Vendas Espécie (%)</Label>
            <div className="flex items-center gap-4 mt-2">
              <input type="range" value={acrescimoEspecie} onChange={(e) => setAcrescimoEspecie(Number(e.target.value))} max={100} min={0} step={1} className="flex-1 accent-primary" />
              <span className="font-mono">{acrescimoEspecie}%</span>
            </div>
          </div>
          <div className="bg-muted p-4 rounded-lg flex flex-col sm:flex-row justify-between items-center font-bold text-lg gap-2 text-center sm:text-left">
            <span>Total Bruto Estimado:</span><span className="text-2xl sm:text-lg">R$ {totalComEspecie.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Passo 2: Rateio Serviço vs Consumo</CardTitle>
          <CardDescription>Defina quanto pertence a serviços (Ignorado) vs consumo (NFC-e).</CardDescription>
        </CardHeader>
        <CardContent>
          <Label>Porcentagem de Serviço (ISS)</Label>
          <div className="flex items-center gap-4 mt-2">
            <input type="range" value={percentualServico} onChange={(e) => setPercentualServico(Number(e.target.value))} max={100} min={0} step={1} className="flex-1 accent-primary" />
            <span className="font-mono">{percentualServico}%</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 text-center">
            <div className="bg-blue-50 p-4 rounded border-blue-100 flex flex-col items-center">
              <span className="text-sm text-blue-600 mb-1">Serviços (Ignorado)</span>
              <span className="text-2xl sm:text-xl font-bold text-blue-800">R$ {totalServico.toFixed(2)}</span>
            </div>
            <div className="bg-orange-50 p-4 rounded border-orange-100 flex flex-col items-center">
              <span className="text-sm text-orange-600 mb-1">Consumo (NFC-e)</span>
              <span className="text-2xl sm:text-xl font-bold text-orange-800">R$ {totalConsumo.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const step2Content = (
    <div className="space-y-6 fade-in">
      <Card>
        <CardHeader>
          <CardTitle>Passo 3: Distribuição do Consumo e Lotes</CardTitle>
          <CardDescription>Como o consumo será rateado nas notas fiscais.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Quantidade de Notas Desejadas</Label>
              <Input type="number" min="1" className="h-12 sm:h-10 text-lg" value={qtdNotasEfetiva} disabled={identificarCliente} onChange={(e) => setQtdNotas(Math.max(1, parseInt(e.target.value) || 1))} />
              {identificarCliente && <span className="text-xs text-muted-foreground mt-1 block">Bloqueado para 1 nota ao identificar o consumidor.</span>}
            </div>
            <div>
              <Label className="mb-2 block">Preço de Venda do Almoço (Ex: Buffet/Kg) R$</Label>
              <Input type="number" min="0.01" step="0.01" className="h-12 sm:h-10 text-lg" value={precoBaseAlmoco} onChange={(e) => setPrecoBaseAlmoco(Math.max(0.01, parseFloat(e.target.value) || 1))} />
            </div>
            <div className="sm:col-span-2">
              <Label className="mb-2 block">% Almoço (no Valor Unitário)</Label>
              <div className="flex items-center gap-4 mt-2 h-10">
                <input type="range" value={percentualAlmoco} onChange={(e) => setPercentualAlmoco(Number(e.target.value))} max={100} min={0} step={1} className="flex-1 accent-primary" />
                <span className="font-mono">{percentualAlmoco}%</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-center mt-6">
            <div className="bg-muted p-4 rounded-lg flex flex-col items-center">
              <span className="text-sm text-muted-foreground mb-1">Meta Almoço ({percentualAlmoco}%)</span>
              <span className="font-bold text-2xl">R$ {totalAlmocoUnitario.toFixed(2)}</span>
            </div>
            <div className="bg-muted p-4 rounded-lg flex flex-col items-center">
              <span className="text-sm text-muted-foreground mb-1">Meta Bebidas ({100 - percentualAlmoco}%)</span>
              <span className="font-bold text-2xl">R$ {totalBebidasFinanceiroUnitario.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const step3Content = (
    <div className="space-y-6 fade-in">
      <Card>
        <CardHeader>
          <CardTitle>Passo 4: Rateio de Bebidas de UMA Nota</CardTitle>
          <CardDescription>Defina as bebidas de CADA nota para fechar os R$ {totalBebidasFinanceiroUnitario.toFixed(2)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {estoque.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma bebida detectada no estoque ao vivo.</p>
          ) : (
            <div className="space-y-4">
              {estoque.map((bebida) => {
                const sel = bebidasSelecionadas[bebida.id] || { qtd: 0, preco: bebida.precoFiscal || 0 };
                return (
                  <div key={bebida.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border rounded-lg gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono font-bold bg-muted px-1.5 py-0.5 rounded text-foreground border">
                          Cód {bebida.codigo || bebida.id}
                        </span>
                        <p className="font-semibold">{bebida.nome}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">Estoque: {bebida.quantidadeAtual}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 font-mono">NCM {bebida.ncm}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 font-mono font-medium">CFOP {bebida.cfop || "5405"}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300 font-mono font-medium">CSOSN {bebida.csosn || "500"}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto items-center mt-2 sm:mt-0">
                      <div className="relative w-full sm:w-32">
                        <span className="absolute left-3 top-3 sm:top-2 text-muted-foreground text-sm">R$</span>
                        <Input type="number" inputMode="decimal" min="0" className="w-full pl-8 h-12 sm:h-10" value={sel.preco || ""} onChange={(e) => atualizarBebida(bebida.id, "preco", Number(e.target.value))} onBlur={(e) => salvarPrecoFiscal(bebida.id, Number(e.target.value))} />
                      </div>
                      <span className="text-muted-foreground">x</span>
                      <Input type="number" inputMode="numeric" min="0" className="w-24 h-12 sm:h-10 text-center" value={sel.qtd || ""} onChange={(e) => atualizarBebida(bebida.id, "qtd", Number(e.target.value))} />
                    </div>
                    <div className="w-full sm:w-24 text-right font-bold text-primary text-lg sm:text-base mt-2 sm:mt-0">R$ {(sel.qtd * sel.preco).toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className={`p-4 rounded-lg flex flex-col sm:flex-row justify-between items-center font-bold mt-6 ${totalAlmocoEfetivoUnitario < 0 ? "bg-red-100 text-red-900 border border-red-200" : "bg-muted"}`}>
            <span>Composição Final da Nota:</span>
            <div className="text-center sm:text-right mt-2 sm:mt-0">
              <span className={totalAlmocoEfetivoUnitario < 0 ? "text-red-700" : ""}>Almoço Efetivo: R$ {totalAlmocoEfetivoUnitario.toFixed(2)}</span>
              <span className="block text-sm text-muted-foreground font-normal mt-1">Bebidas: R$ {totalBebidasCalculadoUnitario.toFixed(2)}</span>
              <span className="block text-sm font-bold text-primary mt-1 border-t border-gray-300 pt-1">Total da Nota: R$ {valorPorNota.toFixed(2)}</span>
              {totalAlmocoEfetivoUnitario < 0 && <span className="block text-xs text-red-600 mt-1">Erro: Bebidas ultrapassaram o valor da nota!</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-lg border-2 border-primary/20 bg-card hover:border-primary/50 transition-colors">
        <CardHeader className="bg-primary/5 rounded-t-lg pb-4">
          <CardTitle className="text-xl">Identificação do Cliente na Nota (Opcional)</CardTitle>
          <CardDescription>Se desejar, informe os dados para as notas saírem com CPF/CNPJ.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 mb-4">
            <input type="checkbox" id="identificar" className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary" checked={identificarCliente} onChange={(e) => setIdentificarCliente(e.target.checked)} />
            <Label htmlFor="identificar" className="text-base cursor-pointer">Quero identificar o consumidor</Label>
          </div>
          {identificarCliente && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 p-4 border rounded-lg bg-muted/20">
              <div className="space-y-2">
                <Label>CPF ou CNPJ</Label>
                <Input placeholder="Somente números (ex: 12345678909)" value={clienteCpfCnpj} onChange={(e) => setClienteCpfCnpj(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Nome Completo (Opcional se CPF)</Label>
                <Input placeholder="Nome do cliente" value={clienteNome} onChange={(e) => setClienteNome(e.target.value)} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const step4Content = loteConfirmacao && (
    <Card className="fade-in shadow-lg border-primary">
      <CardHeader className="bg-primary/5">
        <CardTitle className="text-xl flex items-center gap-2"><Send className="w-5 h-5"/> Conferência Final do Lote</CardTitle>
        <CardDescription>Revise os valores antes de emitir definitivamente na SEFAZ.</CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4 text-sm">
          <div className="flex justify-between font-bold text-base border-b pb-2">
            <span>Total Bruto (Referência):</span><span>R$ {loteConfirmacao.totalGeralConsumo.toFixed(2)}</span>
          </div>
          <div className="flex justify-between border-b pb-2">
            <span>Quantidade de Notas:</span><span>{loteConfirmacao.qtdNotas} nota(s)</span>
          </div>
          <div className="flex justify-between border-b pb-2 text-primary font-bold text-base">
            <span>Total Geral a Emitir:</span>
            <span>{loteConfirmacao.qtdNotas} x R$ {loteConfirmacao.valorPorNota.toFixed(2)} = R$ {loteConfirmacao.totalGeralEmitido.toFixed(2)}</span>
          </div>
          <div className="pt-2">
            <p className="font-semibold mb-2">Composição de UMA Nota:</p>
            <div className="bg-muted p-3 rounded-lg space-y-2">
              <div className="flex justify-between items-center text-sm">
                <div>
                  <span className="font-mono text-xs bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded mr-1.5 font-bold">
                    Cód {loteConfirmacao.almoco.codigo || "00000001"}
                  </span>
                  <span className="font-medium">{loteConfirmacao.almoco.quantidade}x {loteConfirmacao.almoco.nome} (R$ {loteConfirmacao.almoco.precoUnitario.toFixed(2)}/un)</span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300 font-mono font-medium">CFOP {loteConfirmacao.almoco.cfop || "5102"} • CSOSN {loteConfirmacao.almoco.csosn || "103"}</span>
                </div>
                <span className="font-semibold">R$ {loteConfirmacao.almoco.subtotal.toFixed(2)}</span>
              </div>
              {loteConfirmacao.bebidas.map((b, idx) => b.quantidade > 0 && (
                <div key={idx} className="flex justify-between items-center text-muted-foreground text-sm">
                  <div>
                    <span className="font-mono text-xs bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded mr-1.5 font-bold text-foreground">
                      Cód {b.codigo || b.id}
                    </span>
                    <span className="font-medium text-foreground">{b.quantidade}x {b.nome}</span>
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 font-mono font-medium">CFOP {b.cfop || "5405"} • CSOSN {b.csosn || "500"}</span>
                  </div>
                  <span>R$ {b.subtotal.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const abrirModalLote = async (lote) => {
    setLoteModal(lote);
    setStatusNotasLote({});
    setCarregandoStatus(true);

    const baseUrl =
      window.location.hostname === "localhost"
        ? "http://localhost:3001"
        : window.location.hostname.includes("parquetimbo.com.br")
          ? ""
          : REMOTE_SERVER_URL;

    const notasValidas =
      lote.retornoNotaaS?.filter(
        (r) => (r.status >= 200 && r.status < 300) || r.status === 202,
      ) || [];

    const novosStatus = {};
    for (const n of notasValidas) {
      const notaId = n.data?.invoiceId || n.data?.id;
      if (notaId) {
        try {
          const res = await fetch(`${baseUrl}/api/fiscal/status/${notaId}`);
          const json = await res.json();
          if (json.success && json.data) {
            novosStatus[notaId] = json.data;
          } else {
            novosStatus[notaId] = n.data || { status: "desconhecido" };
          }
        } catch (e) {
          novosStatus[notaId] = n.data || { status: "erro_consulta" };
        }
      }
    }
    setStatusNotasLote(novosStatus);
    setCarregandoStatus(false);
  };

  const getBaseUrl = () => {
    return window.location.hostname === "localhost"
      ? "http://localhost:3001"
      : window.location.hostname.includes("parquetimbo.com.br")
        ? ""
        : REMOTE_SERVER_URL;
  };

  const baixarXml = async (lote) => {
    const notasValidas =
      lote.retornoNotaaS?.filter(
        (r) => (r.status >= 200 && r.status < 300) || r.status === 202,
      ) || [];
    if (notasValidas.length === 0) {
      alert("Nenhuma nota autorizada neste lote para download.");
      return;
    }

    try {
      setExportandoZip(true);
      const baseUrl = getBaseUrl();
      await baixarLoteZip(lote, baseUrl, (prog) => {
        setStatusExportacao(prog);
      });
    } catch (err) {
      alert(`Erro ao exportar XML(s): ${err.message}`);
    } finally {
      setExportandoZip(false);
    }
  };

  const baixarXmlNotaIndividual = async (nota) => {
    try {
      const baseUrl = getBaseUrl();
      const notaData = nota.data || {};
      const notaId = notaData.invoiceId || notaData.id;
      await baixarXmlIndividual({
        notaId,
        xmlDireto: notaData.xml,
        dadosNota: notaData,
        baseUrl,
      });
    } catch (err) {
      alert(`Erro ao baixar XML da nota: ${err.message}`);
    }
  };

  const baixarXmlsPeriodo = async () => {
    if (historicoNotas.length === 0) {
      alert("Nenhum lote fiscal encontrado no período selecionado.");
      return;
    }
    try {
      setExportandoZip(true);
      const baseUrl = getBaseUrl();
      let nomePeriodo = "Consolidado";
      if (filtroRapido === "hoje") nomePeriodo = "Hoje";
      else if (filtroRapido === "7dias") nomePeriodo = "Ultimos_7_Dias";
      else if (filtroRapido === "mes") nomePeriodo = "Mes_Atual";
      else if (dataInicio || dataFim) {
        nomePeriodo = `${dataInicio || "inicio"}_ate_${dataFim || "fim"}`;
      }

      await baixarPeriodoZip(historicoNotas, nomePeriodo, baseUrl, (prog) => {
        setStatusExportacao(prog);
      });
    } catch (err) {
      alert(`Erro na exportação consolidada: ${err.message}`);
    } finally {
      setExportandoZip(false);
    }
  };

  const abrirDanfe = (lote) => {
    const notasValidas =
      lote.retornoNotaaS?.filter(
        (r) => (r.status >= 200 && r.status < 300) || r.status === 202,
      ) || [];
    if (notasValidas.length === 0) {
      alert("Nenhuma nota autorizada neste lote para visualizar o DANFE.");
      return;
    }

    const baseUrl =
      window.location.hostname === "localhost"
        ? "http://localhost:3001"
        : window.location.hostname.includes("parquetimbo.com.br")
          ? ""
          : REMOTE_SERVER_URL;

    notasValidas.forEach((nota, i) => {
      const notaData = nota.data || {};
      const notaId = notaData.invoiceId || notaData.id;

      if (notaId) {
        window.open(`${baseUrl}/api/fiscal/danfe/${notaId}`, "_blank");
        return;
      }

      alert(`Nota ${i + 1} não possui identificador da NotaaS disponível.`);
    });
  };

  return (
    <div className="p-4 sm:p-6 pb-24 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Calculator className="w-8 h-8 text-primary" />
        <h1 className="text-2xl sm:text-3xl font-bold">Módulo Fiscal</h1>
      </div>

      <Tabs defaultValue="nova" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 h-12">
          <TabsTrigger value="nova" className="text-base h-full"><FileText className="w-4 h-4 mr-2"/> Nova Emissão</TabsTrigger>
          <TabsTrigger value="historico" className="text-base h-full"><History className="w-4 h-4 mr-2"/> Histórico de Notas</TabsTrigger>
        </TabsList>

        <TabsContent value="nova" className="space-y-6">
          {/* Indicador de Passos */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex flex-col items-center flex-1 relative">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mb-2 transition-colors ${currentStep >= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {step}
                </div>
                <span className="text-xs text-muted-foreground hidden sm:block text-center">
                  {step === 1 && "Entradas"}
                  {step === 2 && "Distribuição"}
                  {step === 3 && "Produtos"}
                  {step === 4 && "Disparo"}
                </span>
                {step < 4 && <div className={`hidden sm:block absolute h-[2px] w-full left-[50%] top-4 -z-10 ${currentStep > step ? 'bg-primary' : 'bg-muted'}`} />}
              </div>
            ))}
          </div>

          {currentStep === 1 && step1Content}
          {currentStep === 2 && step2Content}
          {currentStep === 3 && step3Content}
          {currentStep === 4 && step4Content}

          <div className="flex flex-col sm:flex-row justify-between pt-6 border-t mt-8 gap-4">
            <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)} disabled={currentStep === 1 || isEnviando} className="w-full sm:w-32 h-12 sm:h-10">
              <ArrowLeft className="w-4 h-4 mr-2"/> Voltar
            </Button>
            {currentStep < 4 ? (
              <Button onClick={() => currentStep === 3 ? preAvancarStep4() : setCurrentStep(prev => prev + 1)} className="w-full sm:w-32 h-12 sm:h-10">
                Avançar <ArrowRight className="w-4 h-4 ml-2"/>
              </Button>
            ) : (
              <Button onClick={confirmarEnvio} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto px-8 h-12 sm:h-10" disabled={isEnviando}>
                {isEnviando ? "Enviando..." : <><Send className="w-4 h-4 mr-2"/> Confirmar e Emitir</>}
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="historico" className="space-y-4">
          {/* Métricas e Indicadores Rápidos do Período */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
            <Card className="bg-gradient-to-br from-blue-50/70 to-indigo-50/50 dark:from-slate-900 dark:to-slate-800/80 border-blue-100 dark:border-slate-700 shadow-sm">
              <CardContent className="p-3 sm:p-4 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">Lotes</span>
                  <Layers className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="mt-2">
                  <span className="text-xl sm:text-2xl font-bold text-foreground">{totalLotes}</span>
                  <span className="text-[10px] text-muted-foreground block">disparos</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50/70 to-teal-50/50 dark:from-slate-900 dark:to-slate-800/80 border-emerald-100 dark:border-slate-700 shadow-sm">
              <CardContent className="p-3 sm:p-4 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">Notas</span>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="mt-2">
                  <span className="text-xl sm:text-2xl font-bold text-emerald-700 dark:text-emerald-400">{totalNotasGeral}</span>
                  <span className="text-[10px] text-emerald-600/80 dark:text-emerald-400/80 block font-medium">autorizadas</span>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-2 sm:col-span-1 bg-gradient-to-br from-amber-50/70 to-orange-50/50 dark:from-slate-900 dark:to-slate-800/80 border-amber-100 dark:border-slate-700 shadow-sm">
              <CardContent className="p-3 sm:p-4 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] sm:text-xs font-medium text-muted-foreground">Faturado</span>
                  <DollarSign className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="mt-2">
                  <span className="text-xl sm:text-2xl font-bold text-amber-700 dark:text-amber-400 truncate block">
                    {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(totalValorGeral)}
                  </span>
                  <span className="text-[10px] text-muted-foreground block">total emitido</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Barra de Filtros Ágeis (Pills) */}
          <div className="bg-card border rounded-xl p-3 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <Filter className="w-3.5 h-3.5 text-primary" />
                <span>Filtrar por data:</span>
              </div>
              {(dataInicio || dataFim) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={limparFiltros}
                  className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3 mr-1" /> Limpar Filtro
                </Button>
              )}
            </div>

            {/* Chips de filtro rápido */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              <Button
                variant={filtroRapido === "todos" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs rounded-full px-3"
                onClick={() => aplicarFiltroRapido("todos")}
              >
                Todos
              </Button>
              <Button
                variant={filtroRapido === "hoje" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs rounded-full px-3"
                onClick={() => aplicarFiltroRapido("hoje")}
              >
                Hoje
              </Button>
              <Button
                variant={filtroRapido === "7dias" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs rounded-full px-3"
                onClick={() => aplicarFiltroRapido("7dias")}
              >
                7 Dias
              </Button>
              <Button
                variant={filtroRapido === "mes" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs rounded-full px-3"
                onClick={() => aplicarFiltroRapido("mes")}
              >
                Este Mês
              </Button>
              <Button
                variant={mostrarFiltroAvancado ? "secondary" : "outline"}
                size="sm"
                className="h-8 text-xs rounded-full px-3 flex items-center gap-1"
                onClick={() => aplicarFiltroRapido("custom")}
              >
                <Calendar className="w-3.5 h-3.5" />
                Personalizado
                {mostrarFiltroAvancado ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </Button>
            </div>

            {/* Expansão do filtro por intervalo de datas */}
            {mostrarFiltroAvancado && (
              <div className="pt-2 border-t grid grid-cols-1 sm:grid-cols-2 gap-3 fade-in">
                <div className="space-y-1">
                  <Label className="text-xs">Data Início</Label>
                  <Input
                    type="date"
                    className="h-9 text-xs"
                    value={dataInicio}
                    onChange={(e) => {
                      setDataInicio(e.target.value);
                      setFiltroRapido("custom");
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Fim</Label>
                  <Input
                    type="date"
                    className="h-9 text-xs"
                    value={dataFim}
                    onChange={(e) => {
                      setDataFim(e.target.value);
                      setFiltroRapido("custom");
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Barra de Ação de Exportação Consolidada para Contabilidade */}
          <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-xl p-4 shadow-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-blue-800/40">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <FileArchive className="w-5 h-5 text-blue-300" />
                <h3 className="font-semibold text-sm sm:text-base text-white">
                  Exportação Consolidada (.ZIP + Planilha)
                </h3>
              </div>
              <p className="text-xs text-blue-200/90">
                {totalNotasGeral > 0
                  ? `${totalNotasGeral} nota(s) autorizada(s) no período filtrado prontas para download em arquivo .ZIP com planilha CSV.`
                  : "Nenhuma nota no período selecionado para exportar."}
              </p>
            </div>
            <Button
              onClick={baixarXmlsPeriodo}
              disabled={totalNotasGeral === 0 || exportandoZip}
              className="w-full sm:w-auto h-11 sm:h-10 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center justify-center gap-2 shadow-sm rounded-lg px-5 active:scale-95 transition-all text-xs sm:text-sm flex-shrink-0"
            >
              {exportandoZip ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Empacotando...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Baixar Todos os XMLs ({totalNotasGeral}) .ZIP</span>
                </>
              )}
            </Button>
          </div>

          {/* VISÃO MOBILE: Cards Touch-Friendly */}
          <div className="block md:hidden space-y-3">
            {historicoNotas.length === 0 ? (
              <Card className="p-8 text-center text-muted-foreground">
                <Receipt className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" />
                <p className="font-medium text-base">Nenhum lote fiscal encontrado.</p>
                <p className="text-xs text-muted-foreground mt-1">Ajuste os filtros acima para pesquisar outro período.</p>
              </Card>
            ) : (
              historicoNotas.map((lote) => {
                const resumo = getResumoLote(lote);
                const dataAmigavel = formatarDataAmigavel(lote.dataEmissao);

                return (
                  <div
                    key={lote.id}
                    className="bg-card border rounded-xl p-4 shadow-sm hover:shadow-md transition-all space-y-3 relative overflow-hidden"
                  >
                    {/* Linha indicadora de status no topo do card */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1 ${
                        resumo.isSucesso ? "bg-emerald-500" : "bg-red-500"
                      }`}
                    />

                    {/* Topo do card: Data, Status e Valor */}
                    <div className="flex items-start justify-between gap-2 pt-0.5">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{dataAmigavel}</span>
                        </div>
                        <div>
                          {resumo.isSucesso ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 px-2.5 py-0.5 rounded-full">
                              <CheckCircle2 className="w-3 h-3" />
                              {resumo.qtdNotasValidas} Nota(s) Autorizada(s)
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-red-100 dark:bg-red-950/60 text-red-800 dark:text-red-300 px-2.5 py-0.5 rounded-full">
                              <AlertTriangle className="w-3 h-3" /> Falha no lote
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider block">Total</span>
                        <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400 leading-tight">
                          R$ {resumo.valor.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Detalhes do Lote */}
                    <div className="bg-muted/40 rounded-lg p-2.5 text-xs space-y-1.5 border border-border/40">
                      <div className="flex justify-between items-center text-muted-foreground">
                        <span>Destinatário:</span>
                        <span className="text-foreground font-medium truncate max-w-[190px]">
                          {resumo.cliente}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-muted-foreground">
                        <span>Produtos:</span>
                        <span className="text-foreground font-medium truncate max-w-[190px]">
                          {resumo.textoItens}
                        </span>
                      </div>
                    </div>

                    {/* Barra de Ações Mobile: Grandes e acessíveis para o polegar */}
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 text-xs flex items-center justify-center gap-1 active:scale-95 transition-transform"
                        onClick={() => abrirModalLote(lote)}
                      >
                        <Eye className="w-4 h-4 text-primary" />
                        <span>Detalhes</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-11 text-xs flex items-center justify-center gap-1 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/20 active:scale-95 transition-transform"
                        onClick={() => baixarXml(lote)}
                      >
                        <Download className="w-4 h-4 text-blue-600" />
                        <span>XML</span>
                      </Button>
                      <Button
                        size="sm"
                        className="h-11 text-xs bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center justify-center gap-1 shadow-sm active:scale-95 transition-transform"
                        onClick={() => abrirDanfe(lote)}
                      >
                        <Printer className="w-4 h-4" />
                        <span>DANFE</span>
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* VISÃO DESKTOP: Tabela Corporativa Refinada */}
          <div className="hidden md:block">
            <Card>
              <CardContent className="p-0">
                {historicoNotas.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground space-y-2">
                    <Receipt className="w-12 h-12 mx-auto text-muted-foreground/40" />
                    <p className="font-medium text-base">Nenhum lote emitido recentemente.</p>
                    <p className="text-xs text-muted-foreground">Tente alterar o período nos filtros acima.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30">
                        <TableHead>Data / Hora</TableHead>
                        <TableHead>Destinatário / Resumo</TableHead>
                        <TableHead className="text-center">Qtd Notas</TableHead>
                        <TableHead className="text-right">Valor Total</TableHead>
                        <TableHead className="text-center">Status SEFAZ</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historicoNotas.map((lote) => {
                        const resumo = getResumoLote(lote);
                        const dataAmigavel = formatarDataAmigavel(lote.dataEmissao);

                        return (
                          <TableRow key={lote.id} className="hover:bg-muted/40 transition-colors">
                            <TableCell className="font-medium whitespace-nowrap text-xs">
                              {dataAmigavel}
                            </TableCell>
                            <TableCell className="text-xs">
                              <div className="font-medium text-foreground">{resumo.cliente}</div>
                              <div className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                                {resumo.textoItens}
                              </div>
                            </TableCell>
                            <TableCell className="text-center font-mono text-xs">
                              {lote.notasEnviadas}
                            </TableCell>
                            <TableCell className="text-right font-bold text-emerald-600 dark:text-emerald-400 font-mono text-xs">
                              R$ {resumo.valor.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-center">
                              {resumo.isSucesso ? (
                                <span className="text-xs inline-flex items-center gap-1 bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 px-2.5 py-0.5 rounded-full font-medium">
                                  <CheckCircle2 className="w-3 h-3" />
                                  {resumo.qtdNotasValidas} Emitida(s)
                                </span>
                              ) : (
                                <span className="text-xs inline-flex items-center gap-1 bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-300 px-2.5 py-0.5 rounded-full font-medium">
                                  <AlertTriangle className="w-3 h-3" /> Falha
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2.5 text-xs"
                                  title="Consultar status SEFAZ e detalhes"
                                  onClick={() => abrirModalLote(lote)}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1 text-primary" /> Detalhes
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2.5 text-xs text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/20"
                                  title="Baixar arquivo XML da NFC-e"
                                  onClick={() => baixarXml(lote)}
                                >
                                  <Download className="w-3.5 h-3.5 mr-1" /> XML
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 px-2.5 text-xs text-amber-700 hover:text-amber-800 border-amber-200 bg-amber-50/50 dark:bg-amber-950/20"
                                  title="Visualizar / Imprimir DANFE térmico 80mm com QR Code"
                                  onClick={() => abrirDanfe(lote)}
                                >
                                  <Printer className="w-3.5 h-3.5 mr-1" /> DANFE
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes e Status SEFAZ da NotaaS */}
      <Dialog open={!!loteModal} onOpenChange={(open) => !open && setLoteModal(null)}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pb-3 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <Eye className="w-5 h-5 text-primary" />
                Consulta SEFAZ / Detalhes das Notas
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm mt-0.5">
                Status em tempo real, protocolo de autorização, chave de acesso e comprovante térmico de 80mm.
              </DialogDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9 px-3 text-xs flex items-center justify-center gap-1.5 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/30 hover:bg-blue-100 flex-shrink-0"
              onClick={() => baixarXml(loteModal)}
              disabled={exportandoZip}
            >
              <FileArchive className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span>Baixar Lote (.ZIP)</span>
            </Button>
          </DialogHeader>

          {carregandoStatus ? (
            <div className="flex flex-col items-center justify-center p-8 space-y-3 text-muted-foreground">
              <RefreshCw className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm">Consultando status oficial junto à SEFAZ Estadual via NotaaS...</p>
            </div>
          ) : loteModal && (
            <div className="space-y-4 pt-2">
              {(loteModal.retornoNotaaS?.filter(r => (r.status >= 200 && r.status < 300) || r.status === 202) || []).map((nota, idx) => {
                const notaId = nota.data?.invoiceId || nota.data?.id;
                const st = statusNotasLote[notaId] || nota.data || {};
                const baseUrl = window.location.hostname === "localhost"
                  ? "http://localhost:3001"
                  : (window.location.hostname.includes("parquetimbo.com.br") ? "" : REMOTE_SERVER_URL);

                const isIssued = st.status === "issued" || st.codigoStatus === 100 || st.cStat === "100" || st.cStat === 100;
                const isProcessing = st.status === "queued" || st.status === "processing";

                return (
                  <div key={notaId || idx} className="border rounded-xl p-3.5 sm:p-4 space-y-3 bg-muted/20 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-base">
                          {st.numero || st.nNf ? `NFC-e nº ${st.numero || st.nNf}` : `Nota ${idx + 1}`}
                        </span>
                        {isIssued ? (
                          <span className="text-xs bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300 font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Autorizada SEFAZ
                          </span>
                        ) : isProcessing ? (
                          <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> Na Fila / Processando
                          </span>
                        ) : (
                          <span className="text-xs bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> {st.status || "Erro"}
                          </span>
                        )}
                      </div>
                      <div className="text-right font-bold text-lg text-primary">
                        R$ {Number(st.valorTotal || st.vNf || 0).toFixed(2)}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground pt-1">
                      {st.protocolo || st.nProt ? (
                        <div>
                          Protocolo SEFAZ: <strong className="text-foreground">{st.protocolo || st.nProt}</strong>
                        </div>
                      ) : null}
                      {st.dataRecebimento || st.dhRecbto ? (
                        <div>
                          Data/Hora Aut.: <strong className="text-foreground">{new Date(st.dataRecebimento || st.dhRecbto).toLocaleString('pt-BR')}</strong>
                        </div>
                      ) : null}
                    </div>

                    {st.chaveAcesso ? (
                      <div className="bg-background p-2.5 rounded-lg border flex items-center justify-between gap-2">
                        <div className="text-xs font-mono break-all flex-1">
                          <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Chave de Acesso (44 dígitos):</span>
                          <span className="text-foreground select-all">{st.chaveAcesso}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 flex-shrink-0"
                          title="Copiar Chave de Acesso"
                          onClick={() => {
                            navigator.clipboard.writeText(st.chaveAcesso);
                            setCopiadoChave(st.chaveAcesso);
                            setTimeout(() => setCopiadoChave(null), 2000);
                          }}
                        >
                          {copiadoChave === st.chaveAcesso ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    ) : null}

                    {st.motivo || st.xMotivo ? (
                      <div className="text-xs text-muted-foreground italic bg-muted/30 p-2 rounded">
                        "{st.motivo || st.xMotivo}"
                      </div>
                    ) : null}

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t mt-2">
                      {st.urlChave ? (
                        <a
                          href={st.urlChave}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-medium"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Consulta Portal SEFAZ
                        </a>
                      ) : <div />}

                      <div className="grid grid-cols-2 sm:flex sm:items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-10 sm:h-9 text-xs flex items-center justify-center gap-1"
                          onClick={() => baixarXmlNotaIndividual(nota)}
                        >
                          <Download className="w-4 h-4 mr-1 text-blue-600" /> XML
                        </Button>
                        <Button
                          size="sm"
                          className="h-10 sm:h-9 text-xs bg-amber-500 hover:bg-amber-600 text-white font-medium shadow-sm flex items-center justify-center gap-1"
                          onClick={() => window.open(`${baseUrl}/api/fiscal/danfe/${notaId}`, "_blank")}
                        >
                          <Printer className="w-4 h-4 mr-1" /> Cupom Térmico (80mm)
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Progresso da Exportação ZIP */}
      <Dialog open={exportandoZip} onOpenChange={() => {}}>
        <DialogContent className="w-[90vw] max-w-md p-6 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center">
            <FileArchive className="w-6 h-6 animate-pulse" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-center text-lg font-bold">
              Exportando Arquivos XML
            </DialogTitle>
            <DialogDescription className="text-center text-xs sm:text-sm text-muted-foreground">
              Buscando XMLs autorizados da SEFAZ e gerando a planilha de conferência contábil (.CSV)...
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <div className="w-full bg-muted rounded-full h-3 overflow-hidden border">
              <div
                className="bg-primary h-full transition-all duration-300 ease-out"
                style={{ width: `${statusExportacao.percent || 8}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-xs text-muted-foreground font-medium px-1">
              <span className="truncate max-w-[240px]">{statusExportacao.status || "Processando..."}</span>
              <span className="font-mono font-bold text-primary">{statusExportacao.percent || 0}%</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

